import React, { useEffect, useState } from 'react';
import { Row, Col, Tabs, Checkbox, message } from 'antd';
import { TusClientProvider } from 'use-tus';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { AnghamiAdsFormSelector } from '../core/AnghamiAdsFormSelector';
import { AnghamiAdsFormInput } from '../core/AnghamiAdsFormInput';
import { AnghamiAdsButton } from '../core/AnghamiAdsButton';
import './styles.scss';
import { countriesOptions } from './selectorOptions';
import MastheadCampaignUploadBox from './MastheadCampaignUploadBox';
import MastheadCampaignDatePicker from './MastheadCampaignDatePicker';
import { AxiosSingleton } from '../../utils/axiosinstance';
import AnghamiAdsFullPageLoader from '../core/AnghamiAdsFullPageLoader';

const placementOptions = [
  { label: 'Home', value: 'home' },
  { label: 'Search', value: 'search' }
];

const MastheadCampaign = () => {
  const axios = AxiosSingleton.axiosInstance;
  const mode = window.location.href.includes('create') ? 'create' : 'edit';
  const { campaignId } = useParams();
  const [searchParams] = useSearchParams();
  const countryParam = searchParams.get('countryParam');
  const dateParam = moment(searchParams.get('dateParam') || undefined);
  const isDirectCampaign = searchParams.get('direct') === '1';

  const [title, setTitle] = useState('');
  const [placement, setPlacement] = useState<string[]>(['home']);
  const [countries, setCountries] = useState<string[]>(countryParam ? [countryParam] : []);
  const [date, setDate] = useState<any>(dateParam);
  const [deeplink, setDeeplink] = useState();
  const [deeplinkAr, setDeeplinkAr] = useState();
  const [deeplinkFr, setDeeplinkFr] = useState();
  const [image, setImage] = useState('');
  const [imageAr, setImageAr] = useState('');
  const [imageFr, setImageFr] = useState('');
  const [disabledDates, setDisabledDates] = useState([]);
  const [blockedDays, setBlockedDays] = useState({});
  const [isSubmissionLoading, setIsSubmissionLoading] = useState(false);
  const [isFullPageLoaderVisible, setIsFullPageLoaderVisible] = useState(mode === 'edit');
  const [mastheadCampaignId, setMastheadCampaignId] = useState<number | null>(null);
  const [titleTranslationKey, setTitleTranslationKey] = useState<string>('');
  const [bannerUrlTranslationKey, setBannerUrlTranslationKey] = useState<string>('');
  const [ctaUrlTranslationKey, setCtaUrlTranslationKey] = useState<string>('');

  const isSaveButtonDisabled =
    countries.length === 0 ||
    placement.length === 0 ||
    !date ||
    (!image && !imageAr && !imageFr) ||
    Boolean((image && !deeplink) || (imageAr && !deeplinkAr) || (imageFr && !deeplinkFr));

  const navigate = useNavigate();

  const getBlockedDates = (selectedCountries) => {
    if (selectedCountries.length === 0) {
      setBlockedDays({});
      return;
    }

    Promise.all(
      selectedCountries.map((country) =>
        axios.get('/masthead/blocked-dates', {
          params: {
            country
          }
        })
      )
    ).then((responses) => {
      const blockedDaysMap = {};
      responses.forEach(({ data }) => {
        data.data.forEach((el) => {
          blockedDaysMap[el.blocked_day] = true;
        });
      });
      setBlockedDays(blockedDaysMap);
    });
  };

  const saveMastheadCampaign = () => {
    if (isSaveButtonDisabled) return;
    setIsSubmissionLoading(true);

    const titleTranslation = {
      keyText: mode === 'create' ? '' : titleTranslationKey || '',
      en: title || '',
      ar: title || '',
      fr: title || ''
    };

    const bannerUrl = {
      keyText: mode === 'create' ? '' : bannerUrlTranslationKey || '',
      en: image || '',
      ar: imageAr || '',
      fr: imageFr || ''
    };

    const ctaUrl = {
      keyText: mode === 'create' ? '' : ctaUrlTranslationKey || '',
      en: deeplink || '',
      ar: deeplinkAr || '',
      fr: deeplinkFr || ''
    };

    const displayRequestPayload: any = {
      type: 'display',
      title: titleTranslation,
      countries,
      placement,
      day: date?.format('YYYY-MM-DD'),
      banner_url: bannerUrl,
      cta_url: ctaUrl
    };

    if (mode === 'edit' && mastheadCampaignId) {
      displayRequestPayload.id = mastheadCampaignId;
    }

    if (mode === 'create') {
      const createMastheadForCountry = (country: string) => {
        const selectedCountry = [country];
        return axios
          .post('/masthead-campaigns/store', {
            ...displayRequestPayload,
            countries: selectedCountry
          })
          .then(({ data }) => {
            const campaignIdFromApi = data?.id;
            if (!campaignIdFromApi) {
              return Promise.reject(new Error('No campaign id returned'));
            }
            return axios.post('/masthead/store', {
              mastheadid: campaignIdFromApi,
              title,
              countries: selectedCountry,
              start_date: date?.format('YYYY-MM-DD'),
              deeplink,
              deeplinkAr,
              deeplinkFr,
              image,
              imageFr,
              imageAr
            });
          });
      };

      const createMastheadsSequentially = async () => {
        const results: { country: string; error: any }[] = [];
        for (const country of countries) {
          try {
            await createMastheadForCountry(country);
            results.push({ country, error: null });
          } catch (error) {
            results.push({ country, error });
          }
        }
        return results;
      };

      createMastheadsSequentially()
        .then((results) => {
          const failedCreations = results.filter(({ error }) => error);
          if (failedCreations.length === 0) {
            navigate('/masthead-campaigns/list');
          } else {
            const failedCountries = failedCreations.map(({ country }) => country);
            const successfulCreationsCount = results.length - failedCreations.length;
            const failureDetails = failedCreations.map(({ country, error }) => {
              const countryLabel =
                countriesOptions.find(({ id }) => id === country)?.text || country;
              const data = error?.response?.data;
              const errorMessage = data?.error || data?.message;
              return errorMessage ? `${countryLabel}: ${errorMessage}` : countryLabel;
            });
            const successMessage =
              successfulCreationsCount > 0
                ? ` ${successfulCreationsCount} other ${
                    successfulCreationsCount === 1 ? 'country was' : 'countries were'
                  } created successfully.`
                : '';

            setCountries(failedCountries);
            message.error(`Failed to create ${failureDetails.join('; ')}.${successMessage}`);
          }
          setIsSubmissionLoading(false);
        });
    } else {
      const requests = [];
      if (!isDirectCampaign) {
        requests.push(
          axios.post('/masthead/update', {
            id: campaignId,
            title,
            countries,
            start_date: date?.format('YYYY-MM-DD'),
            deeplink,
            deeplinkAr,
            deeplinkFr,
            image,
            imageFr,
            imageAr
          })
        );
      }
      if (mastheadCampaignId) {
        requests.push(axios.post('/masthead-campaigns/update', displayRequestPayload));
      }
      Promise.all(requests)
        .then(() => {
          navigate('/masthead-campaigns/list');
          setIsSubmissionLoading(false);
        })
        .catch(({ response }) => {
          const { data } = response;
          if (data?.error) {
            message.error(data.error);
          } else {
            message.error('Something went wrong');
          }
          setIsSubmissionLoading(false);
        });
    }
  };

  const getBookings = (selectedCountries) => {
    if (selectedCountries.length === 0) {
      setDisabledDates([]);
      return;
    }

    axios
      .get('/masthead/bookings', {
        params: {
          countries: selectedCountries,
          start_date: moment().format('YYYY-MM-DD'),
          end_date: moment().add(12, 'months').format('YYYY-MM-DD')
        }
      })
      .then(({ data }) => {
        setDisabledDates(
          data?.data?.map((booking) => moment(booking.startdate).format('YYYY-MM-DD'))
        );
      });
  };

  useEffect(() => {
    if (mode === 'create' || !campaignId) return;

    setIsFullPageLoaderVisible(true);

    if (isDirectCampaign) {
      axios
        .get('/masthead-campaigns/get', { params: { id: campaignId } })
        .then(({ data }) => {
          const mc = data?.data;
          const mcCountries = mc.countries ? mc.countries.split(',') : [];
          setMastheadCampaignId(Number(campaignId));
          setTitle(mc.title?.en);
          setCountries(mcCountries);
          setDate(moment(mc.day));
          setDeeplink(mc.cta_url?.en);
          setDeeplinkAr(mc.cta_url?.ar);
          setDeeplinkFr(mc.cta_url?.fr);
          setImage(mc.banner_url?.en);
          setImageAr(mc.banner_url?.ar);
          setImageFr(mc.banner_url?.fr);
          setTitleTranslationKey(mc.title_translation_key || '');
          setBannerUrlTranslationKey(mc.banner_url_translation_key || '');
          setCtaUrlTranslationKey(mc.cta_url_translation_key || '');
          setPlacement(mc.placement ? mc.placement.split(',') : ['home']);
          getBookings(mcCountries);
          getBlockedDates(mcCountries);
          setIsFullPageLoaderVisible(false);
        })
        .catch(() => {
          setIsFullPageLoaderVisible(false);
        });
      return;
    }

    axios
      .get('/masthead/get', {
        params: {
          id: campaignId
        }
      })
      .then(({ data }) => {
        const campaignData = data?.data;
        setTitle(campaignData.title);
        setCountries(campaignData.usercountries);
        setDate(moment(campaignData.startdate));
        setDeeplink(campaignData.deeplink);
        setDeeplinkAr(campaignData.deeplinkAr);
        setDeeplinkFr(campaignData.deeplinkFr);
        setImage(campaignData.image);
        setImageAr(campaignData.imageAr);
        setImageFr(campaignData.imageFr);
        if (campaignData.mastheadid != null) {
          setMastheadCampaignId(campaignData.mastheadid);
          return axios
            .get('/masthead-campaigns/get', {
              params: { id: campaignData.mastheadid }
            })
            .then((res) => {
              const mc = res?.data?.data;
              if (mc) {
                setTitleTranslationKey(mc.title_translation_key || '');
                setBannerUrlTranslationKey(mc.banner_url_translation_key || '');
                setCtaUrlTranslationKey(mc.cta_url_translation_key || '');
                setPlacement(mc.placement ? mc.placement.split(',') : ['home']);
              }
            })
            .catch(() => {})
            .then(() => ({ campaignData }));
        }
        return Promise.resolve({ campaignData });
      })
      .then(({ campaignData }) => {
        getBookings(campaignData.usercountries);
        getBlockedDates(campaignData.usercountries);
        setIsFullPageLoaderVisible(false);
      })
      .catch(() => {
        setIsFullPageLoaderVisible(false);
      });
  }, []);

  if (isFullPageLoaderVisible) {
    return <AnghamiAdsFullPageLoader />;
  }

  return (
    <div className="masthead-campaign-container">
      <div className="masthead-campaign-title">
        {mode === 'create' ? 'Setup Your New Masthead Campaign' : 'Edit Your Masthead Campaign'}
      </div>
      <Row gutter={[16, 16]} style={{ marginBottom: '1rem', marginTop: '1rem' }}>
        <Col span={12}>
          <AnghamiAdsFormInput
            label="Campaign Name"
            placeholder="Insert campaign name"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
            }}
          />
        </Col>
      </Row>
      <Row gutter={[16, 16]} style={{ marginBottom: '1rem' }}>
        <Col span={12}>
          <div className="campaign-form-input-container">
            <label>Placement*</label>
            <div style={{ marginTop: '0.5rem' }}>
              <Checkbox.Group
                options={placementOptions}
                value={placement}
                onChange={(value) => {
                  setPlacement(value as string[]);
                }}
              />
            </div>
          </div>
        </Col>
      </Row>
      <div className="masthead-campaign-section-title">Market and Time</div>
      <div className="masthead-campaign-section-subtitle">
        Please insert the targeted countries and the day you want your masthead to be visible
      </div>
      <AnghamiAdsFormSelector
        label={mode === 'create' ? 'Countries*' : 'Country*'}
        placeholder={mode === 'create' ? 'Select countries' : 'Select country'}
        mode={mode === 'create' ? 'multiple' : undefined}
        value={countries}
        options={countriesOptions}
        onChange={(value) => {
          const selectedCountries = mode === 'create' ? [...value] : [value];
          setCountries(selectedCountries);
          setDate(undefined);
          getBookings(selectedCountries);
          getBlockedDates(selectedCountries);
        }}
      />
      <div
        className="campaign-form-input-container"
        style={{ height: '4.7rem', marginTop: '1rem' }}
      >
        <label>Day*</label>
        <MastheadCampaignDatePicker
          date={date}
          setDate={setDate}
          disabledDates={disabledDates}
          blockedDays={blockedDays}
        />
      </div>
      <div className="masthead-campaign-section-title">Masthead Creative</div>
      <div className="masthead-campaign-section-subtitle">
        Please upload your masthead image(s). make sure that the image have the following specs
        850x330, otherwise the masthead request will not be approved
      </div>
      <Tabs defaultActiveKey="1">
        <Tabs.TabPane tab="English" key="item-1">
          <Row gutter={[16, 16]} style={{ marginBottom: '1rem' }}>
            <Col span={12}>
              <div style={{ marginBottom: '1rem' }}>
                <AnghamiAdsFormInput
                  label="Deeplink"
                  placeholder="Insert english deeplink"
                  value={deeplink}
                  onChange={(e) => {
                    setDeeplink(e.target.value);
                  }}
                />
              </div>
              <TusClientProvider>
                <MastheadCampaignUploadBox
                  uploadSuccessHandler={(fileLocation) => {
                    setImage(fileLocation);
                  }}
                  clearUploadHandler={() => {
                    setImage('');
                  }}
                  fileName={image}
                  inputID="image"
                  key="image"
                />
              </TusClientProvider>
            </Col>
          </Row>
          <div style={{ marginTop: '1rem' }}>
            {image && <img src={image} style={{ height: '165px', width: '425px' }} />}
          </div>
        </Tabs.TabPane>
        <Tabs.TabPane tab="Arabic" key="item-2">
          <Row gutter={[16, 16]} style={{ marginBottom: '1rem' }}>
            <Col span={12}>
              <div style={{ marginBottom: '1rem' }}>
                <AnghamiAdsFormInput
                  label="Deeplink"
                  placeholder="Insert arabic deeplink"
                  value={deeplinkAr}
                  onChange={(e) => {
                    setDeeplinkAr(e.target.value);
                  }}
                />
              </div>
              <TusClientProvider>
                <MastheadCampaignUploadBox
                  uploadSuccessHandler={(fileLocation) => {
                    setImageAr(fileLocation);
                  }}
                  clearUploadHandler={() => {
                    setImageAr('');
                  }}
                  fileName={imageAr}
                  inputID="imageAr"
                  key="imageAr"
                />
              </TusClientProvider>
            </Col>
          </Row>
          <div style={{ marginTop: '1rem' }}>
            {imageAr && <img src={imageAr} style={{ height: '165px', width: '425px' }} />}
          </div>
        </Tabs.TabPane>
        <Tabs.TabPane tab="French" key="item-3">
          <Row gutter={[16, 16]} style={{ marginBottom: '1rem' }}>
            <Col span={12}>
              <div style={{ marginBottom: '1rem' }}>
                <AnghamiAdsFormInput
                  label="Deeplink"
                  placeholder="Insert french deeplink"
                  value={deeplinkFr}
                  onChange={(e) => {
                    setDeeplinkFr(e.target.value);
                  }}
                />
              </div>
              <TusClientProvider>
                <MastheadCampaignUploadBox
                  uploadSuccessHandler={(fileLocation) => {
                    setImageFr(fileLocation);
                  }}
                  clearUploadHandler={() => {
                    setImageFr('');
                  }}
                  fileName={imageFr}
                  inputID="imageFr"
                  key="imageFr"
                />
              </TusClientProvider>
            </Col>
          </Row>
          <div style={{ marginTop: '1rem' }}>
            {imageFr && <img src={imageFr} style={{ height: '165px', width: '425px' }} />}
          </div>
        </Tabs.TabPane>
      </Tabs>
      <AnghamiAdsButton
        style={{ marginTop: '1rem', marginBottom: '1rem' }}
        disabled={isSaveButtonDisabled}
        label="Save"
        onClick={saveMastheadCampaign}
        isLoading={isSubmissionLoading}
      />
    </div>
  );
};

export default MastheadCampaign;
